import { AfterViewChecked, ChangeDetectorRef, Component, NgZone, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { IonRouterOutlet, IonicModule, ModalController, Platform } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AbstractComponentMobile, UiMobileModule } from '@web/ui-mobile';
import { take, timer } from 'rxjs';
import { register } from 'swiper/element/bundle';
import { environment } from '../environments/environment';
import { LoadingData } from './models/loading-data.model';
import { RatingModalComponent } from './pages/rating-modal/rating-modal.component';
import { ClientService } from './services/client.service';
import { LoadingService } from './services/loading.service';
import { compareVersionName } from './utils/version-utils';

register();

@Component({
    selector: 'web-root',
    templateUrl: 'app.component.html',
    imports: [IonicModule, UiMobileModule, TranslateModule],
})
export class AppComponent extends AbstractComponentMobile implements OnInit, AfterViewChecked {
    public override tag = '[AppComponent]';

    public loadingData?: LoadingData;
    protected tmpLoadingData?: LoadingData;

    protected currentAppVersion: string | undefined = undefined;
    protected availableAppVersion: string | undefined = undefined;

    public isUpdateAlertOpen = false;

    public alertButtons: { text: string; role: string; handler: () => void; cssClass: string }[] = [];

    constructor(
        public platform: Platform,
        public translateService: TranslateService,
        private router: Router,
        private zone: NgZone,
        private loadingService: LoadingService,
        private cdRef: ChangeDetectorRef,
        private clientService: ClientService,
        private modalCtrl: ModalController,
        @Optional() private routerOutlet?: IonRouterOutlet,
    ) {
        super();

        console.log(environment.env);

        this.subs['getTradAlertButton'] = this.translateService
            .get('COMPONENTS.UPDATE_ALERT.UPDATE')
            .subscribe(trad => {
                this.alertButtons = [
                    {
                        text: trad,
                        role: 'confirm',
                        handler: () => {
                            this.openAppStore();
                        },
                        cssClass: 'primary-button',
                    },
                ];
            });

        this.initializeApp();
    }

    ngOnInit(): void {
        this.subs['loadingData'] = this.loadingService.loadingData$.subscribe({
            next: (value?: LoadingData) => {
                this.tmpLoadingData = value;
            },
            error: (err: any) => console.error(err),
        });
    }

    // https://stackoverflow.com/questions/43513421/ngif-expression-has-changed-after-it-was-checked
    // https://medium.com/better-programming/expressionchangedafterithasbeencheckederror-in-angular-what-why-and-how-to-fix-it-c6bdc0b22787
    ngAfterViewChecked() {
        if (this.loadingData !== this.tmpLoadingData) {
            this.loadingData = this.tmpLoadingData;
            this.cdRef.detectChanges();
        }
    }

    private async configStatusBar() {
        if ((this.platform.is('android') || Capacitor.getPlatform() === 'android') && this.platform.is('capacitor')) {
            await StatusBar.setStyle({ style: Style.Dark });
            await StatusBar.setBackgroundColor({ color: '#101010' });
        }
    }

    // Using Lottie Splash Screen with Capacitor:
    // https://www.youtube.com/watch?v=JfX7pBq1YG4
    //
    // FIX: Rectangle noir sur iOS avant l'affichage de l'animation :
    // https://github.com/timbru31/cordova-plugin-lottie-splashscreen/issues/453
    private async showSplash() {
        const lottie = (window as any).lottie;

        if ((this.platform.is('ios') || Capacitor.getPlatform() === 'ios') && lottie) {
            await lottie.splashscreen.hide();
            await lottie.splashscreen.show('public/assets/anim/sunology_splash_anim.json', false);
        }
    }

    private async initializeApp(): Promise<void> {
        await this.platform.ready();

        console.log('App Ready!');

        this.configStatusBar();

        this.showSplash();

        this.checkAppUpdate();

        App.addListener('backButton', ({ canGoBack }) => {
            if (!canGoBack) {
                App.exitApp();
            }
        });

        App.addListener('resume', () => {
            this.checkAppUpdate();
        });

        // Deep link from mail or qrcode
        // https://capacitorjs.com/docs/guides/deep-links#angular
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.zone.run(() => {
                // Must match host where the .well-known files are uploaded
                // Currently on stream.sunology.eu
                const slug = event.url.split('.eu').pop();
                if (slug) {
                    this.router.navigateByUrl(slug);
                }
                // If no match, do nothing - let regular routing
                // logic take over
            });
        });

        this.subs[this.tag + 'checkPromptRating'] = timer(30 * 1000, -1)
            .pipe(take(1))
            .subscribe(() => {
                this.subs[this.tag + 'checkPromptRating']?.unsubscribe();
                if (this.clientService.shouldPromptRating) {
                    this.presentRatingModal();
                }
            });
    }

    private checkAppUpdate() {
        if (Capacitor.getPlatform() !== 'web') {
            Promise.all([this.getCurrentAppVersion(), this.getAvailableAppVersion()]).then(values => {
                this.currentAppVersion = values[0];
                this.availableAppVersion = values[1];
                if (values[0] && values[1] && compareVersionName(values[0], values[1]) === 1) {
                    this.showAlert(true);
                }
            });
        }
    }

    public async openAppStore(): Promise<void> {
        await AppUpdate.openAppStore({ country: 'FR' });
    }

    private getCurrentAppVersion = async () => {
        const result = await AppUpdate.getAppUpdateInfo({ country: 'FR' });
        if (Capacitor.getPlatform() === 'android') {
            return result.currentVersionCode;
        } else {
            return result.currentVersionName;
        }
    };

    private getAvailableAppVersion = async () => {
        const result = await AppUpdate.getAppUpdateInfo({ country: 'FR' });
        if (Capacitor.getPlatform() === 'android') {
            return result.availableVersionCode;
        } else {
            return result.availableVersionName;
        }
    };

    public showAlert(open: boolean) {
        this.isUpdateAlertOpen = open;
    }

    async presentRatingModal(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: RatingModalComponent,
            initialBreakpoint: 0.9,
            handle: false,
        });
        await modal.present();
    }
}
